import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as careReasonsService from '@/services/careReasonsService'
import { useAuth } from '@/hooks/useAuth'
import { useLogAction } from '@/hooks/useLogAction'
import {
  CARE_REASONS_KEY,
  INFINITE_CARE_REASONS_KEY,
} from '@/constants/queryKeys'
import type { CareReason } from '@/types/models'

function invalidateCareReasonCaches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: [CARE_REASONS_KEY] })
  queryClient.invalidateQueries({ queryKey: [INFINITE_CARE_REASONS_KEY] })
}

export function useCareReasons() {
  const { churchId } = useAuth()

  return useQuery({
    queryKey: [CARE_REASONS_KEY, churchId],
    queryFn: () => careReasonsService.getCareReasons(churchId!),
    enabled: !!churchId,
  })
}

export function useCareReason(id: string | undefined) {
  return useQuery({
    queryKey: [CARE_REASONS_KEY, id],
    queryFn: () => careReasonsService.getCareReason(id!),
    enabled: !!id,
  })
}

export function useAddCareReason() {
  const queryClient = useQueryClient()
  const { churchId } = useAuth()
  const log = useLogAction()

  return useMutation({
    mutationFn: (
      data: Omit<
        CareReason,
        'id' | 'churchId' | 'createdAt' | 'updatedAt' | 'completedAt'
      >,
    ) => careReasonsService.addCareReason({ ...data, churchId: churchId! }),
    onSuccess: (reasonId, variables) => {
      invalidateCareReasonCaches(queryClient)
      log({
        action: 'careReason.create',
        entityType: 'careReason',
        entityId: reasonId,
        details: { title: variables.title },
      })
    },
  })
}

export function useUpdateCareReason() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<Omit<CareReason, 'id'>>
    }) => careReasonsService.updateCareReason(id, data),
    onSuccess: (_, variables) => {
      invalidateCareReasonCaches(queryClient)
      log({
        action: 'careReason.update',
        entityType: 'careReason',
        entityId: variables.id,
      })
    },
  })
}

export function useDeleteCareReason() {
  const queryClient = useQueryClient()
  const log = useLogAction()

  return useMutation({
    mutationFn: (id: string) => careReasonsService.deleteCareReason(id),
    onSuccess: (_, id) => {
      invalidateCareReasonCaches(queryClient)
      log({
        action: 'careReason.delete',
        entityType: 'careReason',
        entityId: id,
      })
    },
  })
}
